import { ChangeDetectorRef, Injectable, inject } from '@angular/core';
import { groupBy, mapValues, maxBy, toArray } from 'lodash-es';
import { BehaviorSubject, Subject } from 'rxjs';
import { TimeBlockHttpService } from 'src/app/core/state/time-blocks/time-block-entries/time-block-http.service';
import { DateTimeHelper } from 'src/app/features/calendar/util/date-time-helper';
import { AbsencePlannerEventModel } from '../../../core/models/absence-planner/absence-planner-event.model';
import { AbsencePlannerModel } from '../../../core/models/absence-planner/absence-planner.model';
import { GlobalAppConfigModel } from '../../../core/models/app-config/global-app-config.model';
import { UserModel } from '../../../core/models/user/user.model';
import { mapToArray } from '../../../shared/functions/map-functions';
import { IFixIntervalViewType } from '../../calendar/components/time-block/types/time-block-types';
import { EventSlotDirective } from '../directives/event-slot.directive';
import { PlanerWeek } from '../utils/absence-planner-types';
import { AbsencePlannerParallelIndexCalculator } from './absence-planner-parallel-index-calculator.service';

@Injectable({
  providedIn: 'root',
})
export class AbsencePlannerService {
  public plannerModel = new AbsencePlannerModel();
  public maxParallelIndexPerGroupChanged$ = new BehaviorSubject(new Map<number, number>()); // key: userId, number: maxParallelIndex
  public createEventInteractionInProgress$ = new BehaviorSubject<boolean>(false);
  public dragOrResizeInteractionInProgress$ = new BehaviorSubject<boolean>(false);
  public readonly timeBlockHttpService = inject(TimeBlockHttpService);
  public readonly reloadPlannerEvents$ = new Subject<void>();
  private readonly absencePlannerParallelIndexCalculator = inject(
    AbsencePlannerParallelIndexCalculator,
  );

  initPlannerModel(members: UserModel[], globalAppOptions: GlobalAppConfigModel) {
    this.plannerModel.plannerStartOfMonth = DateTimeHelper.startOfMonth(new Date());
    this.plannerModel.plannerEndOfMonth = DateTimeHelper.endOfMonth(new Date());
    this.plannerModel.members = [...members];
    this.plannerModel.weekStartsOn = globalAppOptions.weekStartsOn;
    this.plannerModel.workTimeStart = DateTimeHelper.timeStringToDate(
      globalAppOptions.workTimeStart,
    );
    this.plannerModel.workTimeEnd = DateTimeHelper.timeStringToDate(globalAppOptions.workTimeEnd);
    this.plannerModel.plannerEventModels.set([]);

    this.setPlannerWeeks();
  }

  public setPlannerWeeks(): void {
    const startOfMonth = DateTimeHelper.startOfMonth(this.plannerModel.plannerStartOfMonth);
    const endOfMonth = DateTimeHelper.endOfMonth(this.plannerModel.plannerStartOfMonth);

    const allWeeks = DateTimeHelper.eachWeekOfInterval(startOfMonth, endOfMonth, {
      weekStartsOn: this.plannerModel.weekStartsOn,
    });

    const absencePlannerWeeks: PlanerWeek[] = [];

    allWeeks.forEach((week) => {
      const startDate = DateTimeHelper.startOfWeek(week, {
        weekStartsOn: this.plannerModel.weekStartsOn,
      });
      const endDate = DateTimeHelper.endOfWeek(week, {
        weekStartsOn: this.plannerModel.weekStartsOn,
      });

      const absencePlannerWeek: PlanerWeek = {
        weekNumber: DateTimeHelper.weekNumber(startDate),
        startDate,
        endDate,
        days: DateTimeHelper.eachDayOfInterval(startDate, endDate),
      };
      absencePlannerWeeks.push(absencePlannerWeek);
    });

    this.plannerModel.plannerWeeks = absencePlannerWeeks;
  }

  public prev(cd: ChangeDetectorRef): void {
    const prevMonthStart = DateTimeHelper.subMonths(this.plannerModel.plannerStartOfMonth, 1);

    this.plannerModel.plannerStartOfMonth = DateTimeHelper.startOfMonth(prevMonthStart);
    this.plannerModel.plannerEndOfMonth = DateTimeHelper.endOfMonth(prevMonthStart);

    this.setPlannerWeeks();
    cd.detectChanges();
    this.reloadPlannerEvents$.next();
  }

  public next(cd: ChangeDetectorRef): void {
    const nextMonthStart = DateTimeHelper.addMonths(this.plannerModel.plannerStartOfMonth, 1);

    this.plannerModel.plannerStartOfMonth = DateTimeHelper.startOfMonth(nextMonthStart);
    this.plannerModel.plannerEndOfMonth = DateTimeHelper.endOfMonth(nextMonthStart);

    this.setPlannerWeeks();
    cd.detectChanges();
    this.reloadPlannerEvents$.next();
  }

  public today(cd: ChangeDetectorRef): void {
    const today = new Date();

    this.plannerModel.plannerStartOfMonth = DateTimeHelper.startOfMonth(today);
    this.plannerModel.plannerEndOfMonth = DateTimeHelper.endOfMonth(today);

    this.setPlannerWeeks();
    cd.detectChanges();
    this.reloadPlannerEvents$.next();
  }

  public calculateMaxParallelIndexPerGroup(): void {
    const maxParallelIndexPerGroup = new Map<number, number>(
      this.plannerModel.members.map((user) => [user.id, 0]),
    );

    const grouped = groupBy(this.plannerModel.plannerEventModels(), 'userId');
    Object.entries(grouped).forEach(([, events]) => {
      this.absencePlannerParallelIndexCalculator.calculate(events);
    });

    const maxByGroup = mapValues(grouped, (group) =>
      maxBy(group, 'timeBlockViewType.parallelIndex'),
    );
    const maxByGroupArr = toArray(maxByGroup);

    maxByGroupArr.map((plannerEventModel) => {
      // Set row height for the corresponding user.
      const maxParallelIndex = (plannerEventModel.timeBlockViewType as IFixIntervalViewType)
        .parallelIndex;
      maxParallelIndexPerGroup.set(plannerEventModel.userId, maxParallelIndex);
    });
    this.maxParallelIndexPerGroupChanged$.next(maxParallelIndexPerGroup);
  }

  public calculateRowHeights(memberId: number, minimumRowHeightminimumRowHeight: number): number {
    if (this.plannerModel.plannerEventModels().length === 0) {
      return -1;
    }

    const firstPlannerEvent = this.plannerModel.plannerEventModels()[0];
    const maxParallelIndex = this.maxParallelIndexPerGroupChanged$.value.get(memberId);
    return this.calculateRowHeight(
      firstPlannerEvent,
      maxParallelIndex,
      minimumRowHeightminimumRowHeight,
    );
  }

  public clearAllPlannerEventComponents(): void {
    const m = this.plannerModel;
    const slotsArr = mapToArray(m.eventSlots, true) as EventSlotDirective[];
    slotsArr.forEach((slots) => {
      slots.vcr.clear();
    });

    this.plannerModel.plannerEventModels.set([]);
  }

  private calculateRowHeight(
    plannerEventModel: AbsencePlannerEventModel,
    maxParallelIndex: number,
    minimumRowHeight: number,
  ): number {
    const plannerEventHeight = plannerEventModel.componentRef.instance
      .eventWrapper()
      .nativeElement.getBoundingClientRect().height;
    ++maxParallelIndex;

    const heightRequired = plannerEventHeight * maxParallelIndex + maxParallelIndex * 2;

    if (heightRequired < minimumRowHeight) {
      return minimumRowHeight;
    }

    return heightRequired;
  }
}
